import { useSession } from "@saas/auth/hooks/use-session";
import { useCredits, creditsQueryKey } from "@saas/credits/hooks/use-credits";
import { useRouter } from "@shared/hooks/router";
import { useLocalStorage } from "@shared/hooks/use-local-storage";
import { apiClient } from "@shared/lib/api-client";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import type { AIModel, GeneratedPrompt, PromptLanguage } from "../types";

export function useImageToPrompt() {
	const [imageUrl, setImageUrl] = useState<string>(
		"/images/examples/astronaut.webp",
	);
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreviewUrl, setImagePreviewUrl] = useState<string>(
		"/images/examples/astronaut.webp",
	);
	const [isDefaultImage, setIsDefaultImage] = useState<boolean>(true); // Track if using default example image
	const [selectedModel, setSelectedModel] = useState<AIModel>("general");
	const [promptLanguage, setPromptLanguage] = useState<PromptLanguage>("en");
	const [generatedPrompt, setGeneratedPrompt] = useState<string>("");
	const [promptHistory, setPromptHistory] = useState<GeneratedPrompt[]>([]);
	const { savePrompt } = useLocalStorage();
	const { user } = useSession();
	const router = useRouter();
	const queryClient = useQueryClient();
	const {
		getRemainingCredits,
		hasUnlimitedCredits,
		useCredit,
		isUsingCredit,
	} = useCredits();

	// Convert image to Base64
	const convertImageToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => {
				const base64String = reader.result as string;
				const base64 = base64String.split(",")[1];
				resolve(base64);
			};
			reader.onerror = (error) => reject(error);
		});
	};

	// Handle image selection
	const handleImageSelected = (file: File | null, url: string) => {
		if (file) {
			setImageFile(file);
			const previewUrl = URL.createObjectURL(file);
			setImagePreviewUrl(previewUrl);
			setImageUrl("");
			setIsDefaultImage(false); // User has uploaded a new image
		} else if (url) {
			setImageUrl(url);
			setImagePreviewUrl(url);
			setImageFile(null);
			setIsDefaultImage(false); // User has provided a new URL
		} else {
			// 清除所有图片状态，恢复到默认
			setImageFile(null);
			setImageUrl("/images/examples/astronaut.webp");
			setImagePreviewUrl("/images/examples/astronaut.webp");
			setIsDefaultImage(true); // Back to default image
		}
	};

	// Generate prompt mutation
	const generatePromptMutation = useMutation({
		mutationFn: async (requestData: {
			imageUrl?: string;
			imageBase64?: string;
			model: AIModel;
			language: PromptLanguage;
		}) => {
			const response = await apiClient["image-to-prompt"].$post({
				json: requestData,
			});

			if (!response.ok) {
				throw new Error("Failed to generate prompt");
			}

			return response.json();
		},
		onSuccess: async (data) => {
			const newPrompt = data.prompt;
			const originalPrompt = data.originalPrompt;
			setGeneratedPrompt(newPrompt);

			// 积分已在后端扣除，这里只需要刷新积分显示
			// 删除前端的积分扣除，避免重复扣费
			// await useCredit("IMAGE_TO_TEXT");
			
			// 刷新积分数据以立即显示最新的积分余额
			await queryClient.invalidateQueries({ queryKey: creditsQueryKey });

			// Save to IndexedDB
			if (imageFile) {
				const base64 = await convertImageToBase64(imageFile);
				await savePrompt(
					newPrompt,
					originalPrompt,
					base64,
					selectedModel,
				);
			} else if (imageUrl) {
				const response = await fetch(imageUrl);
				const blob = await response.blob();
				const reader = new FileReader();
				const base64 = await new Promise<string>((resolve) => {
					reader.onload = () => {
						const base64String = reader.result as string;
						resolve(base64String.split(",")[1]);
					};
					reader.readAsDataURL(blob);
				});
				await savePrompt(
					newPrompt,
					originalPrompt,
					base64,
					selectedModel,
				);
			}

			// Add to history
			const historyItem: GeneratedPrompt = {
				model: selectedModel,
				prompt: newPrompt,
				language: promptLanguage,
				timestamp: new Date(),
			};

			setPromptHistory((prev) => [historyItem, ...prev]);
		},
		onError: (error) => {
			toast.error("Error generating prompt");
			console.error(error);
		},
	});

	// Handle generate prompt
	const handleGeneratePrompt = async () => {
		if (generatePromptMutation.isPending || isUsingCredit) return;

		// Check if user is authenticated
		if (!user) {
			const returnUrl = "/image-to-prompt";
			router.push(
				`/auth/login?redirectTo=${encodeURIComponent(returnUrl)}`,
			);
			return;
		}

		// 检查是否有足够的图像到文本信用额度
		const isUnlimited = hasUnlimitedCredits("IMAGE_TO_TEXT");
		const remainingCredits = getRemainingCredits("IMAGE_TO_TEXT");
		if (!isUnlimited && remainingCredits <= 0) {
			toast.error("You have used all your credits");
			return;
		}

		if (!imageFile && !imageUrl) {
			toast.error("Please upload an image or provide an image URL");
			return;
		}

		try {
			const requestData: {
				imageUrl?: string;
				imageBase64?: string;
				model: AIModel;
				language: PromptLanguage;
			} = {
				model: selectedModel,
				language: promptLanguage,
			};

			if (imageFile) {
				const base64 = await convertImageToBase64(imageFile);
				requestData.imageBase64 = base64;
			} else if (imageUrl) {
				requestData.imageUrl = imageUrl;
			}

			generatePromptMutation.mutate(requestData);
		} catch (error) {
			toast.error("Error preparing image data");
			console.error(error);
		}
	};

	// 获取剩余信用额度，对无限额度进行处理
	const getRemainingCreditsFormatted = () => {
		if (hasUnlimitedCredits("IMAGE_TO_TEXT")) {
			return "infinity"; // 特殊标记，表示无限
		}
		return getRemainingCredits("IMAGE_TO_TEXT");
	};

	return {
		imageUrl,
		imageFile,
		imagePreviewUrl,
		isDefaultImage,
		selectedModel,
		promptLanguage,
		generatedPrompt,
		promptHistory,
		usesRemaining: getRemainingCreditsFormatted(),
		hasUnlimitedCredits: () => hasUnlimitedCredits("IMAGE_TO_TEXT"),
		isPending: generatePromptMutation.isPending || isUsingCredit,
		handleImageSelected,
		handleGeneratePrompt,
		setSelectedModel,
		setPromptLanguage,
		setPromptHistory,
	};
}
